import { Link } from '@tanstack/react-router'
import { useAllEdges, useAllProjects, useAllTasks } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { taskActions } from '@/lib/actions'
import { todayLocal, fmtDateInPhrase } from '@/lib/time'
import { householdOf, memberToday, type AddedStanding } from '@/lib/memberToday'
import { useAccess } from '@/state/access'
import { useUi } from '@/state/ui'
import { QuickAdd } from '@/components/QuickAdd'
import { Button, cx } from '@/components/ui'
import type { TaskResource } from '@/kinds/task/apply'

/** A task's time on its day, as typed ("09:00"); nothing for an untimed one. */
const Tick = ({ task }: { task: TaskResource }) => {
  const done = task.status.state === 'done'
  return (
    <button
      type="button"
      aria-label={done ? `Undo ${task.spec.title}` : `Done: ${task.spec.title}`}
      onClick={() => void (done ? taskActions.reopen(task) : taskActions.complete(task))}
      className={cx(
        'h-11 w-11 shrink-0 rounded-full border-2 flex items-center justify-center text-base transition-colors',
        done ? 'border-line text-faint' : 'border-line-strong hover:border-accent hover:bg-accent-soft',
      )}
      data-testid="member-tick"
    >
      {done ? '✓' : ''}
    </button>
  )
}

const Row = ({
  task,
  note,
  tick = true,
  light,
}: {
  task: TaskResource
  note?: string
  tick?: boolean
  light?: boolean
}) => (
  <li className="flex items-center gap-3 px-3 py-2 min-h-16" data-testid="member-row">
    {tick && <Tick task={task} />}
    <Link to="/tasks/$id" params={{ id: task.id }} className="flex-1 min-w-0 py-1">
      <div className={cx('text-[17px] leading-6 break-words', light && 'text-faint line-through')}>
        {task.spec.title}
      </div>
      {note && <div className="text-[13px] text-faint">{note}</div>}
    </Link>
    {task.spec.scheduled_time && (
      <span className={cx('tabular text-[15px] shrink-0', light ? 'text-faint' : 'text-fg-2')}>
        {task.spec.scheduled_time}
      </span>
    )}
  </li>
)

const standingText = (s: AddedStanding): string =>
  s.kind === 'done' ? 'Done' : s.kind === 'moved' ? `Moved to ${fmtDateInPhrase(s.to)}` : 'Open'

const Section = ({
  title,
  testid,
  children,
}: {
  title: string
  testid: string
  children: React.ReactNode
}) => (
  <section className="mb-7" data-testid={testid}>
    <h2 className="label mb-2 px-1 text-muted">{title}</h2>
    {children}
  </section>
)

/**
 * Today for a workspace member: what is for today, what she added and where it
 * stands, what she is kept posted on. The rest of the app is the real one.
 */
export const MemberToday = () => {
  const grant = useAccess((s) => s.grant)
  const tasks = useAllTasks() ?? []
  const projects = useAllProjects() ?? []
  const edges = useAllEdges()
  const setQuickAdd = useUi((s) => s.setQuickAdd)
  const now = useNow()
  const today = todayLocal()
  const household = householdOf(projects)
  const projectOf = new Map(edges.filter((e) => e.relation === 'belongs_to').map((e) => [e.from_id, e.to_id]))
  const titles = new Map(projects.map((p) => [p.id, p.spec.title]))
  const m = memberToday({ tasks, projects, edges, grant, today })
  const where = (t: TaskResource) => {
    const p = projectOf.get(t.id)
    return p && p !== household?.id ? titles.get(p) : undefined
  }

  return (
    <div data-testid="member-today">
      <header className="flex items-end justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="t-display">Today</h1>
          <p className="text-[15px] text-muted mt-1">
            {new Date(now).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <span className="t-title2 tabular text-fg-2 pb-0.5">
          {new Date(now).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </span>
      </header>

      <Section title="For today" testid="member-chores">
        {m.chores.length + m.doneToday.length === 0 ? (
          <p className="text-muted text-[15px] px-1">Nothing for today.</p>
        ) : (
          <ul className="group-list">
            {m.chores.map((t) => (
              <Row key={t.id} task={t} note={where(t)} />
            ))}
            {m.doneToday.map((t) => (
              <Row key={t.id} task={t} note={where(t)} light />
            ))}
          </ul>
        )}
        <Button className="mt-3 w-full" onClick={() => setQuickAdd(true)} data-testid="member-add">
          {household ? `Add to ${household.spec.title}` : 'Add something'}
        </Button>
      </Section>

      {m.added.length > 0 && (
        <Section title="What you added" testid="member-added">
          <ul className="group-list">
            {m.added.map(({ task, standing }) => (
              <Row
                key={task.id}
                task={task}
                tick={false}
                light={standing.kind === 'done'}
                note={standingText(standing)}
              />
            ))}
          </ul>
        </Section>
      )}

      {(m.followedProjects.length > 0 || m.followedTasks.length > 0) && (
        <Section title="What you follow" testid="member-following">
          <ul className="group-list">
            {m.followedProjects.map((p) => (
              <li key={p.id}>
                <Link
                  to="/projects/$id"
                  params={{ id: p.id }}
                  className="flex items-center px-3 min-h-14 text-[17px]"
                  data-testid="member-followed-project"
                >
                  {p.spec.title}
                </Link>
              </li>
            ))}
            {m.followedTasks.map((t) => (
              <Row
                key={t.id}
                task={t}
                tick={false}
                light={t.status.state === 'done'}
                note={m.through.get(t.id) ? `Through ${m.through.get(t.id)!.spec.title}` : undefined}
              />
            ))}
          </ul>
        </Section>
      )}
      {/* adds go to the Household when there is one; this stands the shell's own sheet down */}
      {household && <QuickAdd projectId={household.id} />}
    </div>
  )
}
